/**
 * The 2D wall band: what the plan draws for the walls' thickness. Straight
 * from the wall walk, and the same rules as the 3D shell reduced to the plan:
 * a filled band over every solid span of each leaf's square-ended slab (the
 * slabs overlap inside the corners, so the fill must be opaque), the mitered
 * outer line over the solid spans, and a jamb line across the thickness
 * wherever a cutout's edge meets solid wall. At a junction where exactly one
 * side is open, the solid side shows its square end: the cap across the
 * thickness at the slab's end, and the stub's inner edge running there from
 * the inner corner — the opening's jamb. Two openings meeting at a corner
 * leave it open; an opening spanning a whole wall leaves that wall with no
 * band at all. (The inner line itself is the plan's existing room outline.)
 */
import { type FloorPlanRoom, type Point, WALL_THICKNESS } from '@kaiizen/kai-floor-plan';
import { leafPoint, reachesEnd, reachesStart, slabRange, slabSolidSpans, solidSpans, type WallLeaf, wallWalk } from './wallWalk';

export interface WallBand {
  /** One quad (inner a → inner b → outer b → outer a) per solid span of every leaf's slab. */
  fills: Point[][];
  /** The outer line over each solid span. */
  outerRuns: [Point, Point][];
  /** Lines across the thickness, inner point first: cutout edges, and the square end caps at exposed junctions. */
  jambs: [Point, Point][];
  /** The inner edge of an exposed square end, from the inner corner out to the slab's end. */
  stubs: [Point, Point][];
}

/**
 * The point at `s` on a leaf's inner line, or straight across the thickness
 * from it on the outer line. Across means PERPENDICULAR: only at the leaf's
 * two ends does the outer point become the mitered corner vertex, so every
 * cutout mid-wall is a square notch and diagonals appear only where the
 * perimeter turns.
 */
const along = (leaf: WallLeaf, s: number, outer: boolean): Point => {
  const L = leaf.line.length;
  if (outer && s <= 0) return leaf.outerStart;
  if (outer && s >= L) return leaf.outerEnd;
  const inner = s <= 0 ? leaf.innerStart : s >= L ? leaf.innerEnd : {
    x: leaf.innerStart.x + (leaf.innerEnd.x - leaf.innerStart.x) * (s / L),
    y: leaf.innerStart.y + (leaf.innerEnd.y - leaf.innerStart.y) * (s / L),
  };
  return outer ? { x: inner.x + leaf.line.perp.x * WALL_THICKNESS, y: inner.y + leaf.line.perp.y * WALL_THICKNESS } : inner;
};

export function wallBand(shape: FloorPlanRoom): WallBand {
  const walk = wallWalk(shape);
  const n = walk.length;
  const band: WallBand = { fills: [], outerRuns: [], jambs: [], stubs: [] };
  if (!n) return band;
  // The square end of a leaf's slab at one of its ends, as the plan draws it
  // when exposed: the stub's inner edge from the inner corner, and the cap.
  const squareEnd = (leaf: WallLeaf, atStart: boolean) => {
    const [a0, a1] = slabRange(leaf);
    const corner = atStart ? 0 : leaf.line.length, end = atStart ? a0 : a1;
    if (Math.abs(end - corner) > 1e-9) band.stubs.push([leafPoint(leaf, corner, 0), leafPoint(leaf, end, 0)]);
    band.jambs.push([leafPoint(leaf, end, 0), leafPoint(leaf, end, 1)]);
  };
  walk.forEach((leaf, i) => {
    const L = leaf.line.length;
    for (const [a, b] of slabSolidSpans(leaf)) {
      band.fills.push([leafPoint(leaf, a, 0), leafPoint(leaf, b, 0), leafPoint(leaf, b, 1), leafPoint(leaf, a, 1)]);
    }
    for (const [a, b] of solidSpans(L, leaf.cutouts)) band.outerRuns.push([along(leaf, a, true), along(leaf, b, true)]);
    for (const c of leaf.cutouts) {
      if (!reachesStart(c)) band.jambs.push([along(leaf, c.s0, false), along(leaf, c.s0, true)]);
      if (!reachesEnd(c, L)) band.jambs.push([along(leaf, c.s1, false), along(leaf, c.s1, true)]);
    }
    // The junction with the previous leaf: where exactly one side is open, the solid side's square end shows.
    const prev = walk[(i - 1 + n) % n];
    const openBefore = prev.cutouts.some(c => reachesEnd(c, prev.line.length));
    const openAfter = leaf.cutouts.some(reachesStart);
    if (openBefore && !openAfter) squareEnd(leaf, true);
    if (openAfter && !openBefore) squareEnd(prev, false);
  });
  return band;
}
